import { Divider } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import ResizableDrawer from "components/ResizableDrawer";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { entityKeys, stepKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ComplexEntityTypes } from "../../../../utils/constants";
import AssetsSection from "../../controls/components/AssetsSection/AssetsSection";
import ComplexAffectedEntitiesSection from "../../controls/components/ControlMisconfiguration/relatedEntities/RelatedEntitiesSection";
import StepDrawer from "../../steps/components/StepDrawer";
import ThreatIntel from "../../steps/components/ThreatIntel";
import HeaderTechnique from "./HeaderTechnique";
import TechniqueStepsTable from "./TechniqueStepsTable";

const TechniqueDrawer = ({ selectedTechnique, showRisk }) => {
  const colors = useColors();

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedStepId, setSelectedStepId] = useState(null);
  const [showDetailedAffectedEntities, setShowDetailedAffectedEntities] =
    useState(false);

  const {
    fetchStepsBatch,
    fetchEntitiesBatch,
    trackEvent,
    getEntitiesOverview,
  } = useAPI();

  const onDrawerClose = () => {
    searchParams.delete("technique");
    setSearchParams(searchParams.toString());
  };

  const { data: entitiesOverview } = useQuery({
    queryKey: entityKeys.entitiesOverview(),
    queryFn: getEntitiesOverview,
    select: (response) => response.data,
  });

  const { data: relatedSteps, isLoading: isLoadingSteps } = useQuery({
    queryKey: stepKeys.batchSteps(selectedTechnique?.related_step_ids || []),
    queryFn: async () => {
      const steps = (await fetchStepsBatch(selectedTechnique?.related_step_ids))
        .data;
      return steps;
    },
    enabled: selectedTechnique?.related_step_ids?.length > 0,
  });

  // Extract complex entity IDs from related steps using useMemo instead of useState + useEffect
  const relatedMisconfiguredEntityIds = useMemo(() => {
    if (!relatedSteps || relatedSteps.length === 0) return [];

    const allEntityIds = relatedSteps.reduce((ids, step) => {
      if (
        step.misconfigured_entity_ids &&
        step.misconfigured_entity_ids.length > 0
      ) {
        // Only add entity IDs that start with complex entity types
        const complexEntityIds = step.misconfigured_entity_ids.filter((id) =>
          Object.values(ComplexEntityTypes).some((type) => id.startsWith(type))
        );
        ids.push(...complexEntityIds);
      }
      return ids;
    }, []);

    // Remove duplicates using Set and convert back to array
    return Array.from(new Set(allEntityIds));
  }, [relatedSteps]);

  const {
    data: complexMisconfiguredEntitiesByType,
    isLoading: isLoadingMisconfigured,
  } = useQuery({
    queryKey: entityKeys.entitiesByTechnique(selectedTechnique?.technique_id),
    queryFn: async () => {
      const entities = (
        await fetchEntitiesBatch(
          relatedMisconfiguredEntityIds,
          "technique-" + selectedTechnique?.technique_id
        )
      ).data;
      return entities;
    },
    select: (response) => {
      const entities = response;
      const entitiesByType = {};
      entities.forEach((entity) => {
        if (entity.entity_type in entitiesByType) {
          entitiesByType[entity.entity_type].push(entity);
        } else {
          entitiesByType[entity.entity_type] = [entity];
        }
      });
      return entitiesByType;
    },
    enabled:
      !!selectedTechnique?.technique_id &&
      relatedMisconfiguredEntityIds.length > 0 &&
      !isLoadingSteps,
  });

  const openTechniqueUrl = () => {
    const technique_id = searchParams.get("technique");
    trackEvent(
      "Clicked on MITRE ATT&CK Technique from Technique drawer: " + technique_id
    );
    const formattedItem = technique_id.includes(".")
      ? technique_id.replace(".", "/")
      : technique_id;
    window.open(
      `https://attack.mitre.org/techniques/${formattedItem}`,
      "_blank"
    );
  };

  const onStepClick = (step_id) => {
    searchParams.set("step_id", step_id);
    setSearchParams(searchParams.toString());
  };

  useEffect(() => {
    const localSearchParams = new URLSearchParams(location.search);
    const step_id = localSearchParams.get("step_id");

    if (step_id) {
      if (!Number.isNaN(Number(step_id))) {
        setSelectedStepId(step_id);
      }
    } else {
      setSelectedStepId(null);
    }
  }, [location]);

  const onStepDrawerClose = () => {
    trackEvent("User closed step drawer from technique drawer");
    const params = new URLSearchParams(location.search);
    params.delete("step_id");
    setSearchParams(params.toString());
  };

  // Determine if we're in a loading state
  const isLoading =
    isLoadingSteps ||
    (relatedMisconfiguredEntityIds.length > 0 && isLoadingMisconfigured);

  return (
    <ResizableDrawer
      initialWidth={820}
      minWidth={820}
      PaperProps={{
        style: {
          borderRadius: "8px 0 0 0",
        },
      }}
      anchor="right"
      open={selectedTechnique !== null}
      onClose={onDrawerClose}
    >
      <Box
        sx={{
          width: "100%",
          padding: "24px",
          backgroundColor: colors.grey_50,
          height: "100%",
          overflowY: "auto",
        }}
      >
        {isLoading && <LoadingIcon size={25} />}
        {!isLoading && selectedTechnique && (
          <Box display="flex" flexDirection="column" gap={4}>
            <Box>
              <HeaderTechnique
                openTechniqueUrl={openTechniqueUrl}
                colors={colors}
                selectedTechnique={selectedTechnique}
                showRisk={showRisk}
              />
              {relatedSteps && (
                <ThreatIntel
                  step={relatedSteps[0]} // One step is enough to show the related threat intel
                  showTechniqueId={false}
                />
              )}
            </Box>
            <Divider />

            {!showDetailedAffectedEntities &&
              relatedMisconfiguredEntityIds.length > 0 && (
                <Box>
                  <AssetsSection
                    entity_ids={relatedMisconfiguredEntityIds}
                    title="What critical assets are affected?"
                    source="technique"
                  />
                </Box>
              )}
            {complexMisconfiguredEntitiesByType ? (
              <ComplexAffectedEntitiesSection
                complexEntitiesByType={complexMisconfiguredEntitiesByType}
                showDetailedAffectedEntities={showDetailedAffectedEntities}
                setShowDetailedAffectedEntities={
                  setShowDetailedAffectedEntities
                }
              />
            ) : null}
            {!showDetailedAffectedEntities && (
              <Box>
                <Typography sx={{ mb: 1 }} variant="subtitle1">
                  Which attack steps are using this technique?
                </Typography>
                {relatedSteps && (
                  <TechniqueStepsTable
                    steps={relatedSteps}
                    onRowClick={onStepClick}
                  />
                )}
                <StepDrawer
                  selectedStepId={selectedStepId}
                  onStepDrawerClose={() => {
                    onStepDrawerClose();
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ResizableDrawer>
  );
};

export default TechniqueDrawer;
